/**
 * WB4.7 — gated kernel dispatch (Popconfirm + revision snapshot before side effects).
 * Single entry for palette · list · inspector · bulk paths.
 */

import type { WorkbenchAction } from '@shared/business-workbench';
import {
  isBulkStateChangeHandlerRef,
  isFinanceCompensatingHandlerRef,
  isHighTierHandlerRef,
  parseFinanceReversalSourceVoucherId,
  requiresPopconfirmGate,
} from '@shared/business-workbench/workbench-action-recoverability';
import {
  buildCancelledExecutionResult,
  buildPendingExecutionResult,
  buildWorkbenchExecutionResult,
  resolveActionRiskTier,
  type WorkbenchActionSafety,
  type WorkbenchExecutionResult,
} from '@shared/business-workbench/workbench-action-safety';
import type { ObjectRef } from '@shared/business-workbench/workbench-focus';
import type { WorkbenchActionExecutionEntry } from '@shared/business-workbench/workbench-action-execution-log';
import {
  dispatchWorkbenchKernelHandlerWithResult,
  type WorkbenchKernelDispatchDeps,
} from './ActionExecutor';
import { requestWorkbenchActionConfirmation } from './workbench-high-tier-action-gate';
import { notifyGatedKernelDispatchCompletion } from './workbench-kernel-dispatch-feedback';
import {
  recordBulkStateChangeWithRevision,
  recordEntityDeleteWithRevision,
  recordFinanceReversalRequest,
  postFinanceReversalAfterApproval,
  recordFlowStartWithRevision,
} from './runtime/workbench-state-change-revision';
import {
  buildFinanceReversalIdempotencyKey,
  claimFinanceReversalRecording,
  releaseFinanceReversalRecording,
} from './runtime/finance-reversal-idempotency';
import { registerFinanceReversalPendingApproval } from './runtime/workbench-finance-reversal-approval-flow';
import { markDeferredHighTierAction } from './runtime/workbench-deferred-high-tier-action';

export function isGatedKernelHandlerRef(handlerRef: string): boolean {
  return (
    requiresPopconfirmGate(handlerRef) ||
    isHighTierHandlerRef(handlerRef) ||
    isFinanceCompensatingHandlerRef(handlerRef) ||
    isBulkStateChangeHandlerRef(handlerRef)
  );
}

function resolveBulkBeforeStates(deps: WorkbenchKernelDispatchDeps) {
  const selectedRefs = deps.selectedRefs ?? [deps.objectRef];
  return selectedRefs.map((ref: ObjectRef) => ({
    objectId: ref.id,
    businessDomain: ref.businessDomain,
    state: deps.resolveRecordSnapshot?.(ref) ??
      deps.entitySnapshot ?? {
        id: ref.id,
        status: 'active',
        ...(ref.label ? { name: ref.label } : {}),
      },
  }));
}

function resolveRiskPolicy(
  action: Pick<WorkbenchAction, 'id' | 'handlerRef'>,
  deps: WorkbenchKernelDispatchDeps
) {
  return resolveActionRiskTier({
    actionId: action.id,
    handlerRef: action.handlerRef,
    businessDomain: deps.objectRef.businessDomain,
  });
}

function buildGatedSuccessResult(
  action: Pick<WorkbenchAction, 'id'>,
  deps: WorkbenchKernelDispatchDeps,
  riskPolicy: WorkbenchActionSafety,
  input?: {
    entry?: WorkbenchActionExecutionEntry;
    revisionId?: string;
    userMessage?: WorkbenchExecutionResult['userMessage'];
    executionPhase?: WorkbenchExecutionResult['executionPhase'];
  }
): WorkbenchExecutionResult {
  const revisionId = input?.revisionId ?? input?.entry?.revisionSnapshotId;
  const executionPhase = input?.executionPhase ?? 'succeeded';
  return buildWorkbenchExecutionResult({
    ok: executionPhase === 'succeeded',
    handled: true,
    executionPhase,
    actionId: action.id,
    objectRef: deps.objectRef,
    riskPolicy,
    ...(revisionId ? { revisionId } : {}),
    ...(input?.entry?.timelineEventId ? { timelineEventId: input.entry.timelineEventId } : {}),
    ...(input?.userMessage ? { userMessage: input.userMessage } : {}),
  });
}

/**
 * Async dispatch with shared recoverability policy.
 * Returns true when the handlerRef was recognized (including user-cancelled gated actions).
 */
export async function dispatchGatedWorkbenchKernelHandler(
  action: Pick<WorkbenchAction, 'id' | 'label' | 'handlerRef'>,
  deps: WorkbenchKernelDispatchDeps
): Promise<boolean> {
  return (await dispatchGatedWorkbenchKernelHandlerWithResult(action, deps)).handled;
}

export async function dispatchGatedWorkbenchKernelHandlerWithResult(
  action: Pick<WorkbenchAction, 'id' | 'label' | 'handlerRef'>,
  deps: WorkbenchKernelDispatchDeps
): Promise<WorkbenchExecutionResult> {
  const riskPolicy = resolveRiskPolicy(action, deps);
  const { handlerRef } = action;

  if (isBulkStateChangeHandlerRef(handlerRef)) {
    const selectionCount = deps.selectionCount ?? 1;
    if (selectionCount < 2) {
      return buildWorkbenchExecutionResult({
        ok: false,
        handled: false,
        actionId: action.id,
        objectRef: deps.objectRef,
        riskPolicy,
      });
    }

    if (requiresPopconfirmGate(handlerRef)) {
      const confirmed = await requestWorkbenchActionConfirmation({
        handlerRef,
        actionLabel: action.label,
      });
      if (!confirmed) {
        return buildCancelledExecutionResult({
          actionId: action.id,
          objectRef: deps.objectRef,
          riskPolicy,
        });
      }
    }

    const { entry } = await recordBulkStateChangeWithRevision({
      tenantId: deps.tenantId,
      anchorObjectRef: deps.objectRef,
      action,
      beforeStates: resolveBulkBeforeStates(deps),
      ...(deps.surface ? { surface: deps.surface } : {}),
    });
    if (deps.onBulkStateChange) {
      await deps.onBulkStateChange(handlerRef);
    }
    return buildGatedSuccessResult(action, deps, riskPolicy, { entry });
  }

  if (!isGatedKernelHandlerRef(handlerRef)) {
    return dispatchWorkbenchKernelHandlerWithResult(action, deps);
  }

  if (requiresPopconfirmGate(handlerRef)) {
    const confirmed = await requestWorkbenchActionConfirmation({
      handlerRef,
      actionLabel: action.label,
    });
    if (!confirmed) {
      return buildCancelledExecutionResult({
        actionId: action.id,
        objectRef: deps.objectRef,
        riskPolicy,
      });
    }
  }

  if (handlerRef === 'crud:delete') {
    const { snapshot, entry } = await recordEntityDeleteWithRevision({
      tenantId: deps.tenantId,
      objectRef: deps.objectRef,
      entitySnapshot: deps.entitySnapshot ?? { id: deps.objectRef.id },
      actionLabel: action.label,
      ...(deps.surface ? { surface: deps.surface } : {}),
    });
    markDeferredHighTierAction({
      tenantId: deps.tenantId,
      objectRef: deps.objectRef,
      handlerRef: 'crud:delete',
      actionId: action.id,
      actionLabel: action.label,
    });
    return buildGatedSuccessResult(action, deps, riskPolicy, {
      entry,
      ...(snapshot?.id ? { revisionId: snapshot.id } : {}),
      userMessage: {
        title: '삭제 요청이 기록되었습니다.',
        description: '실제 삭제는 승인 보정 완료 후 진행됩니다.',
      },
    });
  }

  if (handlerRef.startsWith('flow:start:')) {
    const flowTemplateId = handlerRef.slice('flow:start:'.length);
    const { snapshot, entry } = await recordFlowStartWithRevision({
      tenantId: deps.tenantId,
      objectRef: deps.objectRef,
      flowTemplateId,
      actionLabel: action.label,
      ...(deps.flowStartContext !== undefined ? { context: deps.flowStartContext } : {}),
      ...(deps.surface ? { surface: deps.surface } : {}),
    });
    if (deps.onStartFlow) {
      deps.onStartFlow(flowTemplateId);
    } else {
      deps.navigate?.('/workspace');
    }
    return buildGatedSuccessResult(action, deps, riskPolicy, {
      entry,
      ...(snapshot?.id ? { revisionId: snapshot.id } : {}),
    });
  }

  if (isFinanceCompensatingHandlerRef(handlerRef)) {
    const sourceVoucherId = parseFinanceReversalSourceVoucherId(handlerRef);
    if (!sourceVoucherId) {
      return buildWorkbenchExecutionResult({
        ok: false,
        handled: false,
        actionId: action.id,
        objectRef: deps.objectRef,
        riskPolicy,
      });
    }

    const idempotencyKey = buildFinanceReversalIdempotencyKey(sourceVoucherId);
    if (!claimFinanceReversalRecording(idempotencyKey)) {
      return buildWorkbenchExecutionResult({
        ok: true,
        handled: true,
        actionId: action.id,
        objectRef: deps.objectRef,
        riskPolicy,
        userMessage: {
          title: '역분개 요청이 이미 기록되었습니다.',
        },
      });
    }

    try {
      const { snapshot, entry, reversalRequestId, requiresApproval } =
        await recordFinanceReversalRequest({
          tenantId: deps.tenantId,
          objectRef: deps.objectRef,
          sourceVoucherId,
          actionLabel: action.label,
          ...(deps.surface ? { surface: deps.surface } : {}),
        });

      if (requiresApproval) {
        registerFinanceReversalPendingApproval({
          tenantId: deps.tenantId,
          objectRef: deps.objectRef,
          requestId: reversalRequestId,
          sourceVoucherId,
          actionLabel: action.label,
        });
        if (deps.onFinanceReversal) {
          await deps.onFinanceReversal(sourceVoucherId);
        }
        return buildGatedSuccessResult(action, deps, riskPolicy, {
          entry,
          ...(snapshot?.id ? { revisionId: snapshot.id } : {}),
          userMessage: {
            title: '역분개 요청이 기록되었습니다.',
            description:
              '승인 후 전기가 진행됩니다. Finance tier 작업은 toast undo를 사용할 수 없습니다.',
          },
          executionPhase: 'requires_approval',
        });
      }

      await postFinanceReversalAfterApproval({
        tenantId: deps.tenantId,
        requestId: reversalRequestId,
      });

      if (deps.onFinanceReversal) {
        await deps.onFinanceReversal(sourceVoucherId);
      }
      return buildGatedSuccessResult(action, deps, riskPolicy, {
        entry,
        ...(snapshot?.id ? { revisionId: snapshot.id } : {}),
        userMessage: {
          title: '역분개 요청이 기록되었습니다.',
          description: 'Finance tier 작업은 toast undo를 사용할 수 없습니다.',
        },
      });
    } catch (error) {
      releaseFinanceReversalRecording(idempotencyKey);
      throw error;
    }
  }

  return dispatchWorkbenchKernelHandlerWithResult(action, deps);
}

/**
 * Sync-friendly entry: claims gated handlers immediately and runs async gate in background.
 * Low-risk handlers stay synchronous.
 */
export function dispatchWorkbenchKernelHandlerWithGate(
  action: Pick<WorkbenchAction, 'id' | 'label' | 'handlerRef'>,
  deps: WorkbenchKernelDispatchDeps
): boolean {
  return dispatchWorkbenchKernelHandlerWithGateResult(action, deps).handled;
}

export function dispatchWorkbenchKernelHandlerWithGateResult(
  action: Pick<WorkbenchAction, 'id' | 'label' | 'handlerRef'>,
  deps: WorkbenchKernelDispatchDeps
): WorkbenchExecutionResult {
  if (isBulkStateChangeHandlerRef(action.handlerRef)) {
    const selectionCount = deps.selectionCount ?? 1;
    if (selectionCount < 2) {
      return buildWorkbenchExecutionResult({
        ok: false,
        handled: false,
        actionId: action.id,
        objectRef: deps.objectRef,
        riskPolicy: resolveRiskPolicy(action, deps),
      });
    }
  }

  if (isGatedKernelHandlerRef(action.handlerRef)) {
    const riskPolicy = resolveRiskPolicy(action, deps);
    void dispatchGatedWorkbenchKernelHandlerWithResult(action, deps)
      .then((result) => {
        notifyGatedKernelDispatchCompletion(result, action);
      })
      .catch((error: unknown) => {
        notifyGatedKernelDispatchCompletion(
          buildWorkbenchExecutionResult({
            ok: false,
            handled: false,
            executionPhase: 'failed',
            actionId: action.id,
            objectRef: deps.objectRef,
            riskPolicy,
            userMessage: {
              title:
                error instanceof Error ? error.message : '보호된 작업 실행 중 오류가 발생했습니다.',
            },
          }),
          action
        );
      });
    return buildPendingExecutionResult({
      actionId: action.id,
      objectRef: deps.objectRef,
      riskPolicy,
    });
  }

  return dispatchWorkbenchKernelHandlerWithResult(action, deps);
}
